import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Rating,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  ThumbDownAlt as ThumbDownAltIcon,
  ThumbDownAltOutlined as ThumbDownAltOutlinedIcon,
  ThumbUpAlt as ThumbUpAltIcon,
  ThumbUpAltOutlined as ThumbUpAltOutlinedIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

/**
 * In-app course feedback (same API as the public portal).
 * Shows approved customer reviews and allows eligible learners to submit.
 */
const CoursePublicFeedbackPanel = ({ trainingTitleId, courseName, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rating, setRating] = useState(5);
  const [thumb, setThumb] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!trainingTitleId) return;
    setLoading(true);
    try {
      const [eligRes, sumRes] = await Promise.all([
        api.get(`/training/catalog/courses/${trainingTitleId}/public-feedback/eligibility`),
        api.get(`/training/catalog/courses/${trainingTitleId}/public-feedback/summary`)
      ]);
      const elig = eligRes.data?.data || eligRes.data;
      setEligibility(elig);
      setSummary(sumRes.data?.data || elig?.publicSummary || null);
    } catch (e) {
      setEligibility({ error: true, message: e.response?.data?.message || 'Could not load feedback' });
      try {
        const sumRes = await api.get(`/training/catalog/courses/${trainingTitleId}/public-feedback/summary`);
        setSummary(sumRes.data?.data || null);
      } catch {
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, [trainingTitleId]);

  useEffect(() => {
    load();
  }, [load]);

  const my = eligibility?.myFeedback;
  const canSubmit =
    eligibility?.canSubmit === true ||
    (!!eligibility?.authenticated &&
      !!eligibility?.completed &&
      my?.review_status !== 'pending' &&
      my?.review_status !== 'accepted' &&
      eligibility?.reason !== 'pending_review' &&
      eligibility?.reason !== 'already_accepted' &&
      !eligibility?.error);

  const publicSummary = summary || eligibility?.publicSummary;
  const approvedItems = Array.isArray(publicSummary?.items) ? publicSummary.items : [];

  const handleSubmit = async () => {
    if (!rating || rating < 1) {
      toast.error('Please choose a star rating');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/training/catalog/courses/${trainingTitleId}/public-feedback`, {
        rating,
        thumb: thumb || undefined,
        comment: comment.trim() || undefined
      });
      toast.success('Feedback submitted — waiting for instructor approval');
      setComment('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!trainingTitleId) return null;

  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box>
      {!compact && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Course feedback{courseName ? `: ${courseName}` : ''}
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: compact ? 0 : 1 }}>
        Customer reviews
      </Typography>
      {publicSummary && publicSummary.count > 0 ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Approved average:{' '}
            <strong>
              {publicSummary.avgRating != null ? `${publicSummary.avgRating} / 5` : '—'}
            </strong>{' '}
            ({publicSummary.count} {publicSummary.count === 1 ? 'review' : 'reviews'})
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {approvedItems.slice(0, 8).map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Rating value={Number(item.rating) || 0} readOnly size="small" />
                  {item.thumb === 'up' && <ThumbUpAltIcon fontSize="small" color="success" />}
                  {item.thumb === 'down' && <ThumbDownAltIcon fontSize="small" color="error" />}
                  <Typography variant="caption" color="text.secondary">
                    {item.submitter_name || 'Learner'} · Approved
                  </Typography>
                </Stack>
                {item.comment && (
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {item.comment}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No approved customer feedback yet for this course.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Your feedback
      </Typography>

      {eligibility?.error ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {eligibility.message}
        </Alert>
      ) : !eligibility?.completed ? (
        <Alert severity="info">
          Finish all required items for this course, then you can leave a 5★ review. Instructors approve feedback before it
          appears under Customer reviews.
        </Alert>
      ) : my?.review_status === 'pending' || eligibility?.reason === 'pending_review' ? (
        <Alert severity="info">
          Status: <strong>Submitted</strong> — waiting for instructor approval.
        </Alert>
      ) : my?.review_status === 'accepted' || eligibility?.reason === 'already_accepted' ? (
        <Alert severity="success">
          Status: <strong>Approved</strong> — your feedback is published under Customer reviews.
        </Alert>
      ) : null}

      {canSubmit && (
        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Your rating (1–5 stars)
          </Typography>
          <Rating value={rating} onChange={(e, v) => setRating(v || 1)} size="large" />
          <Typography variant="caption" color="text.secondary">
            Thumbs (optional)
          </Typography>
          <ToggleButtonGroup exclusive size="small" value={thumb} onChange={(e, v) => setThumb(v)}>
            <ToggleButton value="up" aria-label="Thumbs up">
              {thumb === 'up' ? <ThumbUpAltIcon /> : <ThumbUpAltOutlinedIcon />}
            </ToggleButton>
            <ToggleButton value="down" aria-label="Thumbs down">
              {thumb === 'down' ? <ThumbDownAltIcon /> : <ThumbDownAltOutlinedIcon />}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Comments (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Submitting…' : 'Submit feedback'}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default CoursePublicFeedbackPanel;
